import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAdmin } from "../context/AdminContext";
import { Lock, Loader2, AlertTriangle } from "lucide-react";

function formatErr(detail) {
  if (!detail) return "Inloggen mislukt.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AdminLoginPage() {
  const { admin, login } = useAdmin();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (admin && admin.role === "admin") {
    return <Navigate to="/admin" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-zinc-50 px-5"
      data-testid="admin-login-page"
    >
      <div className="w-full max-w-md rounded-lg border-2 border-nibro-sage bg-white shadow-xl">
        <div className="border-b-2 border-aramfix-red/30 bg-nibro-sage px-6 py-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/80">
            NIBRO · Admin Panel
          </div>
          <div className="mt-1 font-display text-2xl font-bold text-white">
            Inloggen
          </div>
        </div>
        <form onSubmit={submit} className="space-y-4 px-6 py-7">
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-zinc-600">
              E-mail
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              className="w-full rounded-md border border-zinc-200 bg-white px-3 py-2.5 text-sm focus:border-nibro-sage focus:outline-none focus:ring-2 focus:ring-nibro-sage/30"
              data-testid="admin-login-email"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-zinc-600">
              Wachtwoord
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full rounded-md border border-zinc-200 bg-white px-3 py-2.5 text-sm focus:border-nibro-sage focus:outline-none focus:ring-2 focus:ring-nibro-sage/30"
              data-testid="admin-login-password"
            />
          </div>

          {error && (
            <div
              className="flex items-start gap-2 rounded-md border border-aramfix-red/40 bg-aramfix-red/5 px-3 py-2 text-xs text-aramfix-red"
              data-testid="admin-login-error"
            >
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-md bg-nibro-sage px-4 py-3 text-sm font-bold text-white transition hover:bg-nibro-sage-hover disabled:opacity-50"
            data-testid="admin-login-submit"
          >
            {loading ? (
              <><Loader2 size={14} className="animate-spin" /> Bezig...</>
            ) : (
              <><Lock size={14} /> Inloggen</>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
